// warehouseSvg — the pure codec between a warehouse map's SVG string and its
// geometry model. No Vue, no store: it must run under jsdom (DOMParser) so it
// is unit-testable and usable from the WarehouseMap class getters.
//
// The app's click contract (WarehouseMap.vue) treats every `g.zone` group as
// a zone whose identity is the joined text of its <tspan>s. `parse` reads
// that same structure; `serialize` emits it.

// ---- 2D affine matrix helpers ([a, b, c, d, e, f]) ----
const IDENTITY = [1, 0, 0, 1, 0, 0];

// Line pitch for multi-line zone labels, in ems — shared by the serializer
// and the editor so both stack tspans identically.
export const LABEL_LINE_HEIGHT = 0.9;

function multiply(m1, m2) {
  return [
    m1[0] * m2[0] + m1[2] * m2[1],
    m1[1] * m2[0] + m1[3] * m2[1],
    m1[0] * m2[2] + m1[2] * m2[3],
    m1[1] * m2[2] + m1[3] * m2[3],
    m1[0] * m2[4] + m1[2] * m2[5] + m1[4],
    m1[1] * m2[4] + m1[3] * m2[5] + m1[5],
  ];
}

function apply(m, x, y) {
  return { x: m[0] * x + m[2] * y + m[4], y: m[1] * x + m[3] * y + m[5] };
}

// Uniform scale factor of a matrix — converts a font-size in the group's
// coordinate space into viewBox units.
function scaleFactor(m) {
  return Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]));
}

function parseTransform(str) {
  let m = IDENTITY;
  if (!str) return m;
  const re = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/g;
  let match;
  while ((match = re.exec(str))) {
    const args = match[2]
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);
    let t = IDENTITY;
    switch (match[1]) {
      case "matrix":
        t = args.slice(0, 6);
        break;
      case "translate":
        t = [1, 0, 0, 1, args[0] || 0, args[1] || 0];
        break;
      case "scale":
        t = [
          args[0] || 1,
          0,
          0,
          args.length > 1 ? args[1] : args[0] || 1,
          0,
          0,
        ];
        break;
      case "rotate": {
        const rad = ((args[0] || 0) * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        t = [cos, sin, -sin, cos, 0, 0];
        if (args.length > 2) {
          t = multiply(multiply([1, 0, 0, 1, args[1], args[2]], t), [
            1,
            0,
            0,
            1,
            -args[1],
            -args[2],
          ]);
        }
        break;
      }
      case "skewX": {
        const rad = ((args[0] || 0) * Math.PI) / 180;
        t = [1, 0, Math.tan(rad), 1, 0, 0];
        break;
      }
      case "skewY": {
        const rad = ((args[0] || 0) * Math.PI) / 180;
        t = [1, Math.tan(rad), 0, 1, 0, 0];
        break;
      }
    }
    m = multiply(m, t);
  }
  return m;
}

// Round to 3 decimals so serialize(parse(x)) is bit-identical on re-parse.
function r3(n) {
  // `|| 0` collapses -0 to 0 so serialized output re-parses identically.
  return Math.round(n * 1000) / 1000 || 0;
}

// Parse a path `d` attribute into absolute vertices. The original hand-drawn
// maps drew straight segments with m/M, v/V, h/H, l/L and z; a few floors
// also used curve commands (c/C, s/S, q/Q, t/T, a/A) whose curves are drawn as
// straight lines, so only each curve segment's END point becomes a vertex —
// the control points are ignored. Sub-path breaks (a move after drawing,
// used for door gaps) continue the vertex list — the fill is what renders.
export function pathVertices(d, matrix = IDENTITY) {
  const vertices = [];
  // Numbers consumed per segment by each command letter.
  const SEGMENT_ARGS = { C: 6, S: 4, Q: 4, T: 2, A: 7 };
  const re = /([MmLlHhVvZzCcSsQqTtAa])([^MmLlHhVvZzCcSsQqTtAa]*)/g;
  let match;
  let cx = 0;
  let cy = 0;
  while ((match = re.exec(d || ""))) {
    const cmd = match[1];
    const nums = match[2]
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);
    if (cmd === "Z" || cmd === "z") continue;
    const rel = cmd === cmd.toLowerCase();
    const op = cmd.toUpperCase();
    if (op === "H" || op === "V") {
      nums.forEach((n) => {
        if (op === "H") cx = rel ? cx + n : n;
        else cy = rel ? cy + n : n;
        vertices.push(apply(matrix, cx, cy));
      });
      continue;
    }
    const curveArgs = SEGMENT_ARGS[op];
    if (curveArgs) {
      // Curves render as straight lines: each segment contributes only its
      // end point (the last x/y pair). Intermediate parameters are control
      // points / arc flags and do not move the pen past the segment end.
      for (let i = 0; i + curveArgs <= nums.length; i += curveArgs) {
        const ex = nums[i + curveArgs - 2];
        const ey = nums[i + curveArgs - 1];
        cx = rel ? cx + ex : ex;
        cy = rel ? cy + ey : ey;
        vertices.push(apply(matrix, cx, cy));
      }
      continue;
    }
    // M and L both consume x/y pairs; extra pairs after M are implicit L.
    for (let i = 0; i + 1 < nums.length; i += 2) {
      cx = rel ? cx + nums[i] : nums[i];
      cy = rel ? cy + nums[i + 1] : nums[i + 1];
      vertices.push(apply(matrix, cx, cy));
    }
  }
  return vertices.map((p) => ({ x: r3(p.x), y: r3(p.y) }));
}

function fontSizeOf(el) {
  const attr = el.getAttribute("font-size");
  if (attr) return parseFloat(attr);
  const style = el.getAttribute("style") || "";
  const match = style.match(/font-size\s*:\s*([0-9.]+)/);
  return match ? parseFloat(match[1]) : null;
}

// Label identity: tspans joined by " " — exactly as WarehouseMap.vue computes
// it — with each tspan an authored line.
function labelOf(textEl) {
  const tspans = textEl.querySelectorAll("tspan");
  const lines = tspans.length
    ? Array.from(tspans).map((t) => t.textContent.trim())
    : [textEl.textContent.trim()];
  return { label: lines.join(" ").trim(), lines };
}

// Rotation authored on the <text> element itself (scale() was present in the
// original hand-drawn maps and is ignored).
function rotationOf(textEl) {
  const str = textEl.getAttribute("transform") || "";
  const re = /rotate\(\s*(-?[0-9.]+)/g;
  let match;
  let rotation = 0;
  while ((match = re.exec(str))) {
    let deg = ((Number(match[1]) % 360) + 360) % 360;
    if (deg === 270) deg = -90;
    if (deg === 90 || deg === -90 || deg === 180) rotation = deg;
  }
  return rotation;
}

function directChild(el, tag) {
  return Array.from(el.children).find((c) => c.tagName.toLowerCase() === tag);
}

function parseZone(group, parentMatrix) {
  const m = multiply(
    parentMatrix,
    parseTransform(group.getAttribute("transform"))
  );
  const shapeEl =
    directChild(group, "rect") ||
    directChild(group, "path") ||
    directChild(group, "polygon");
  const textEl = directChild(group, "text");
  if (!shapeEl || !textEl) return null;

  const sm = multiply(m, parseTransform(shapeEl.getAttribute("transform")));
  const tag = shapeEl.tagName.toLowerCase();
  let x;
  let y;
  let w;
  let h;
  let points = [];
  let shape;
  if (tag === "rect") {
    const rx = parseFloat(shapeEl.getAttribute("x")) || 0;
    const ry = parseFloat(shapeEl.getAttribute("y")) || 0;
    const rw = parseFloat(shapeEl.getAttribute("width")) || 0;
    const rh = parseFloat(shapeEl.getAttribute("height")) || 0;
    const corners = [
      apply(sm, rx, ry),
      apply(sm, rx + rw, ry),
      apply(sm, rx + rw, ry + rh),
      apply(sm, rx, ry + rh),
    ];
    const xs = corners.map((p) => p.x);
    const ys = corners.map((p) => p.y);
    x = Math.min(...xs);
    y = Math.min(...ys);
    w = Math.max(...xs) - x;
    h = Math.max(...ys) - y;
    shape = "rect";
  } else if (tag === "polygon") {
    points = (shapeEl.getAttribute("points") || "")
      .split(/[\s]+/)
      .filter(Boolean)
      .map((pair) => {
        const [px, py] = pair.split(",").map(Number);
        const p = apply(sm, px, py);
        return { x: r3(p.x), y: r3(p.y) };
      });
    if (!points.length) return null;
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    x = Math.min(...xs);
    y = Math.min(...ys);
    w = Math.max(...xs) - x;
    h = Math.max(...ys) - y;
    shape = "polygon";
  } else {
    points = pathVertices(shapeEl.getAttribute("d"), sm);
    if (!points.length) return null;
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    x = Math.min(...xs);
    y = Math.min(...ys);
    w = Math.max(...xs) - x;
    h = Math.max(...ys) - y;
    shape = "polygon";
  }

  const tm = multiply(m, parseTransform(textEl.getAttribute("transform")));
  const { label, lines } = labelOf(textEl);
  const fontSize =
    fontSizeOf(textEl) ||
    fontSizeOf(textEl.querySelector("tspan") || textEl) ||
    0;

  return {
    label,
    lines,
    shape,
    x: r3(x),
    y: r3(y),
    w: r3(w),
    h: r3(h),
    points,
    rotation: rotationOf(textEl),
    // Internal: per-zone label size for the mode computation (not part of
    // the public zone shape).
    _labelSize: fontSize * scaleFactor(tm),
  };
}

// Most common value across a list (the mode), for labelSize.
function mode(values) {
  const counts = {};
  let best = values[0];
  let bestCount = 0;
  values.forEach((v) => {
    const key = r3(v);
    counts[key] = (counts[key] || 0) + 1;
    if (counts[key] > bestCount) {
      best = key;
      bestCount = counts[key];
    }
  });
  return best || 0;
}

export function parse(svgString) {
  const doc = new DOMParser().parseFromString(
    String(svgString || ""),
    "image/svg+xml"
  );
  const svg = doc.querySelector("svg");
  if (!svg || doc.querySelector("parsererror")) {
    throw new Error("invalidMapSvg");
  }
  const vb = (svg.getAttribute("viewBox") || "")
    .split(/[\s,]+/)
    .filter(Boolean)
    .map(Number);
  const rawBox =
    vb.length === 4
      ? vb
      : [
          0,
          0,
          parseFloat(svg.getAttribute("width")) || 0,
          parseFloat(svg.getAttribute("height")) || 0,
        ];
  const viewBox = {
    x: r3(rawBox[0]),
    y: r3(rawBox[1]),
    w: r3(rawBox[2]),
    h: r3(rawBox[3]),
  };

  const rootG = directChild(svg, "g");
  const rootMatrix = rootG
    ? parseTransform(rootG.getAttribute("transform"))
    : IDENTITY;
  const floorPath = svg.querySelector("path.floor");
  const floor = floorPath
    ? pathVertices(
        floorPath.getAttribute("d"),
        multiply(
          rootMatrix,
          parseTransform(floorPath.getAttribute("transform"))
        )
      )
    : [];

  const zones = [];
  svg.querySelectorAll("g.zone").forEach((group) => {
    // Accumulate the transform stack from the <svg> down to (but not
    // including) this group; the group's own transform is applied inside
    // parseZone to its children.
    const ancestors = [];
    let node = group.parentElement;
    while (node && node !== svg) {
      ancestors.unshift(node);
      node = node.parentElement;
    }
    let m = IDENTITY;
    ancestors.forEach((el) => {
      m = multiply(m, parseTransform(el.getAttribute("transform")));
    });
    const zone = parseZone(group, m);
    if (zone && zone.label) zones.push(zone);
  });

  const labelSize = mode(zones.map((z) => z._labelSize));
  zones.forEach((z) => delete z._labelSize);

  return {
    viewBox,
    labelSize: r3(labelSize),
    floor,
    zones,
  };
}

export function zoneLabelsFrom(svgString) {
  return parse(svgString).zones.map((zone) => zone.label);
}

// Greedy word wrap at ~0.6·labelSize per character. Explicit "\n" in the
// input always produces a line break.
export function wrapLabel(text, innerWidth, labelSize) {
  const charWidth = Math.max(0.6 * (labelSize || 1), 1e-9);
  const maxChars = Math.max(1, Math.floor(innerWidth / charWidth));
  const lines = [];
  String(text || "")
    .split("\n")
    .forEach((segment) => {
      const words = segment.trim().split(/\s+/).filter(Boolean);
      if (!words.length) {
        lines.push("");
        return;
      }
      let line = "";
      words.forEach((word) => {
        const candidate = line ? `${line} ${word}` : word;
        if (line && candidate.length > maxChars) {
          lines.push(line);
          line = word;
        } else {
          line = candidate;
        }
      });
      if (line) lines.push(line);
    });
  return lines.length ? lines : [""];
}

// The exact CSS the serializer emits — exported so the editor canvas can
// render the same styles inline and stay WYSIWYG by construction. The zone
// stroke scales with the map: the hand-drawn originals use ~0.1em of the
// label size (180EB: stroke 13.5 at font-size 135.66), so it stays a thin
// stem-width line at any scale rather than a screen-px constant.
export function zoneStyle(labelSize) {
  return `.floor{fill:#808080}
.zone rect,.zone polygon{fill:#fff;fill-opacity:.5;stroke:#000;stroke-width:${r3(
    labelSize * 0.1
  )}}
.zone text{fill:#000;font-family:Roboto,sans-serif;text-anchor:middle;dominant-baseline:central}`;
}

function esc(text) {
  return String(text)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

// Canonical serialization — see module docs.
export function serialize(model) {
  const { viewBox, labelSize, floor, zones } = model;
  const fmt = (n) => String(r3(n));
  const out = [];
  out.push(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${fmt(viewBox.x)} ${fmt(
      viewBox.y
    )} ${fmt(viewBox.w)} ${fmt(viewBox.h)}">`
  );
  out.push(`<style>\n${zoneStyle(labelSize)}\n</style>`);
  out.push("<g>");
  if (floor && floor.length) {
    const d = floor
      .map((p, i) => `${i === 0 ? "M" : "L"} ${fmt(p.x)} ${fmt(p.y)}`)
      .join(" ");
    out.push(`<path class="floor" d="${d} Z"/>`);
  }
  out.push("<g>");
  (zones || []).forEach((zone) => {
    const cx = zone.x + zone.w / 2;
    const cy = zone.y + zone.h / 2;
    out.push('<g class="zone">');
    if (zone.shape === "polygon") {
      const pts = (zone.points || [])
        .map((p) => `${fmt(p.x)},${fmt(p.y)}`)
        .join(" ");
      out.push(`<polygon points="${pts}"/>`);
    } else {
      out.push(
        `<rect x="${fmt(zone.x)}" y="${fmt(zone.y)}" width="${fmt(
          zone.w
        )}" height="${fmt(zone.h)}"/>`
      );
    }
    const transform = zone.rotation
      ? ` transform="rotate(${zone.rotation} ${fmt(cx)} ${fmt(cy)})"`
      : "";
    out.push(
      `<text x="${fmt(cx)}" y="${fmt(cy)}" font-size="${fmt(
        labelSize
      )}"${transform}>`
    );
    const lines = zone.lines && zone.lines.length ? zone.lines : [zone.label];
    lines.forEach((line, i) => {
      const dy =
        lines.length > 1
          ? ` dy="${
              i === 0
                ? r3((-(lines.length - 1) / 2) * LABEL_LINE_HEIGHT)
                : LABEL_LINE_HEIGHT
            }em"`
          : "";
      out.push(`<tspan x="${fmt(cx)}"${dy}>${esc(line)}</tspan>`);
    });
    out.push("</text>");
    out.push("</g>");
  });
  out.push("</g>");
  out.push("</g>");
  out.push("</svg>");
  return out.join("\n");
}
